import React, { useMemo } from 'react';
import {
  Box,
  Popover,
  Button,
  IconButton,
  Typography,
  Divider,
  Breadcrumbs,
  Link,
  Select,
  ListItemIcon,
  ListItemText,
  ListItem,
} from '@material-ui/core';
import { Description, Folder } from '@material-ui/icons';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import { useSnackbar } from 'notistack';
import get from 'lodash/get';
import Header from '../../components/Header';
import { Tabs } from '../../components/Tab';
import classes from './Document.module.scss';
import FileManagement from './components/FileManagement';
import DocumentMenu from './components/Menu';
import AddNewModal from './components/Modal/AddNewTab';
import DeleteModal from './components/Modal/DeleteModal';
import ShareFiles from 'views/Product/ProductProfile/components/ShareFiles';
import FileSystemNavigator from 'views/Product/ProductProfile/components/FileSystemNavigator';
import flattenDeep from 'lodash/flattenDeep';
import { TABS, MENUS } from '../../constants';
import SearchBox from '../../components/SearchBox';
import CheckIcon from '@material-ui/icons/Check';
import { SearchWithSuggestionProvider } from 'context';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { ProductFileManagementProvider } from 'context';
import T from 'i18n-react';
import { PRODUCT_MANAGEMENT_PATH } from 'shared/path';
import { setLanguageLocale } from 'util/icwUtility';

setLanguageLocale(T, [PRODUCT_MANAGEMENT_PATH]);

const DocumentSections = ({
  companyId,
  productId,
  productTabs = [],
  onCreateTab,
  onUpdateTab,
  onDeleteTab,
  onChangeTab,
  handleCreateFolder,
  handleCreateFiles,
  onBulkDownload,
  uploadFile,
  shareFile,
  files = {},
  handleUpdateExpiryDate,
  handleUpdateReminder,
  handleDeleteFileOrFolder,
  handleRename,
  handleMoveFileFolder,
  viewLocation,
  searchFile,
  onRefresh,
  isActive,
  onOpenMarkActiveModal,
  ...props
}) => {
  const willDeletedTab = React.useRef([]);
  const isFocusing = React.useRef(false);
  const leftButtonProps = React.useRef();
  const isUndo = React.useRef(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [editMode, setEditMode] = React.useState(false);
  const [editItem, setEditItem] = React.useState(undefined);
  const [addingNewTab, setAddingNewTab] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);
  const [tabs, setTabs] = React.useState(TABS);
  const [activeTab, setActiveTab] = React.useState('inspection');
  const [folders, setFolders] = React.useState([]);
  const [showMore, setShowMore] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openShareFiles, setShareFiles] = React.useState(false);
  const [openFilesNav, setFilesNav] = React.useState(false);
  const [action, setAction] = React.useState('');
  const searchType = React.useRef('all');
  const [currentSearchType, setCurrentSearchType] = React.useState('all');
  const [selected, setSelected] = React.useState({
    inspection: [],
    testing: [],
    document: [],
  });
  React.useEffect(() => {
    const locationTab = viewLocation[0];
    if (locationTab) {
      const tab = tabs.findIndex(({ key }) => key === locationTab);
      if (tab > -1) {
        viewLocation.splice(0, 1);
        setActiveTab(locationTab);
        setCurrentTab(tab);
        const data = get(files, `${locationTab}.fileArr`);
        while (viewLocation.length) {
          const existedFolder = data.find(({ name }) => name === viewLocation[0]);
          if (!existedFolder) break;
          viewLocation.splice(0, 1);
          setFolders([...folders, { ...existedFolder, label: existedFolder.name }]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, files, viewLocation]);

  React.useEffect(() => {
    const restTabs = productTabs
      .map((tab) => ({
        key: tab,
        label: tab,
        content: <FileManagement />,
        editable: true,
      }))
      .filter((tab) => {
        const indexOf = willDeletedTab.current.findIndex(({ value }) => value === tab.key);
        return indexOf < 0;
      });
    setTabs(TABS.concat(restTabs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTabs]);

  const onClickMore = (event) => {
    if (editMode) return;
    setAnchorEl(event.currentTarget);
    setShowMore(true);
  };
  const handleDeleteTab = (item) => {
    setShowDeleteModal(false);
    setEditItem(null);
    setEditMode(false);
    const newTabs = tabs.filter((tab, index) => index !== item.id);
    setTabs([...newTabs]);
    setActiveTab(newTabs[newTabs.length - 1] || activeTab);
    onDeleteTab({ name: item.value });
    enqueueSnackbar('Tab deleted', {
      key: `${item.value}-${item.id}-tab-deleted-success`,
      className: classes.snackbarSuccess,
      autoHideDuration: 8000,
      action: (
        <>
          <IconButton
            onClick={() => {
              closeSnackbar(`${item.value}-${item.id}-tab-deleted-success`);
            }}>
            <CloseIcon />
          </IconButton>
        </>
      ),
    });
  };
  const handleSearch = React.useCallback(
    ({ searchBy, value, searchType = currentSearchType }, cb) => {
      let params = {
        isDeleted: false,
        [`${searchBy}_like`]: `%${value}%`,
      };
      if (searchType === 'current-tab') {
        params['type_like'] = `%${activeTab}%`;
      }
      if (searchType === 'current-folder') {
        const folder = folders[folders.length - 1] ?? { id: 0 };
        params['parentId'] = folder.id;
      }
      searchFile(productId, params, (response) => {
        if (response) {
          const { data } = response.data;
          cb(data);
        }
      });
    },
    [currentSearchType]
  );
  const onCreateFolder = (folderName, cb) => {
    const folder = folders[folders.length - 1] ?? { id: 0 };
    handleCreateFolder(
      {
        parentId: folder?.id ?? 0,
        type: activeTab,
        name: folderName,
      },
      cb
    );
  };
  const onCreateFiles = async (files, cb) => {
    const result = await uploadFile(Array.from(files), `/${companyId}/path`);
    handleCreateFiles(
      {
        fileContents: result,
        parentId: folders[folders.length - 1]?.id ?? 0,
        type: activeTab,
      },
      cb
    );
  };
  const goToFile = (item) => {
    const location = [...item.location];
    const locationTab = location[0];
    if (locationTab) {
      const tab = tabs.findIndex(({ key }) => key === locationTab);
      if (tab > -1) {
        location.splice(0, 1);
        setActiveTab(locationTab);
        setCurrentTab(tab);
        const data = get(files, `${locationTab}.fileArr`);
        while (location.length) {
          const existedFolder = data.find(({ name }) => name === location[0]);
          if (!existedFolder) break;
          location.splice(0, 1);
          setFolders([...folders, { ...existedFolder, label: existedFolder.name }]);
        }
      }
    }
  };
  const transformFiles = useMemo(() => {
    return Object.keys(files).reduce((acc, current) => {
      const { fileArr } = files[current];
      return { ...acc, [current]: fileArr };
    }, {});
  }, [files]);
  React.useEffect(() => {
    const initialSelected = Object.keys(files).reduce((acc, current) => {
      return { ...acc, [current]: [] };
    }, {});
    setSelected({ ...initialSelected, ...selected });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const menusSearchType = React.useMemo(() => {
    const menus = [
      {
        icon: currentSearchType === 'current-folder' ? <CheckIcon></CheckIcon> : null,
        label: 'Current folder',
        action: 'current-folder',
      },
      {
        icon: currentSearchType === 'all' ? <CheckIcon></CheckIcon> : null,
        label: 'All locations',
        action: 'all',
      },
      {
        icon: currentSearchType === 'current-tab' ? <CheckIcon></CheckIcon> : null,
        label: 'Current tab',
        action: 'current-tab',
      },
    ];
    if (folders.length) return menus;
    menus.splice(0, 1);
    return menus;
  }, [currentSearchType, folders]);

  const currentFolder = React.useMemo(() => {
    if (!folders || !folders.length) return { id: 0 };
    return folders[folders.length - 1];
  }, [folders]);

  return (
    <Box className={classes.document}>
      <Box display='flex' flexDirection='row' justifyContent='space-between'>
        <Breadcrumbs separator={<NavigateNextIcon />} className={classes.breadcrumb}>
          <Link color='inherit' href='/product/list'>
            Product List
          </Link>
          {!folders.length ? (
            <Typography variant='body2'>
              Document
              {activeTab ? ' - ' + activeTab : ''}
            </Typography>
          ) : (
            <Link
              color='inherit'
              onClick={() => {
                setFolders([]);
              }}>
              Document
              {activeTab ? ' - ' + activeTab : ''}
            </Link>
          )}
          {folders.map((folder, index) => {
            return index === folders.length - 1 ? (
              <Typography key={folder.id} variant='body2'>
                {folder.label}
              </Typography>
            ) : (
              <Link
                key={folder.id}
                color='inherit'
                onClick={() => {
                  const remaining = folders.slice(0, index + 1);
                  setFolders([...remaining]);
                }}>
                {folder.label}
              </Link>
            );
          })}
        </Breadcrumbs>
        <Box>
          <SearchWithSuggestionProvider
            searchBy='name'
            onSearch={handleSearch}
            renderSuggestionItem={(item, refInput) => {
              return (
                <ListItem
                  button
                  key={item.id}
                  className={classes.fileName}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    goToFile(item);
                  }}>
                  <ListItemIcon>
                    {item.documentListId ? <Description></Description> : <Folder></Folder>}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name || item.documentList.fileName.split(',')[1]}
                    secondary={item.location.join('/')}
                  />
                </ListItem>
              );
            }}>
            {({ onChange, onBlur, onFocus, search, refInput }) => {
              return (
                <SearchBox
                  ref={refInput}
                  onFocus={() => {
                    onFocus();
                  }}
                  onBlur={() => {
                    onBlur();
                  }}
                  onChange={onChange}
                  endAdornment={
                    <Box display='flex' className={classes.endAdornment}>
                      <Divider orientation='vertical' />
                      <Select
                        onBlur={() => {
                          onBlur();
                        }}
                        onFocus={(event) => {
                          event.stopPropagation();
                          setTimeout(() => {
                            onFocus();
                          }, 150);
                        }}
                        autoFocus={false}
                        MenuProps={{
                          PopoverClasses: { paper: classes.popover },
                          getContentAnchorEl: null,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'center',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'center',
                          },
                        }}
                        onChange={(event) => {
                          setCurrentSearchType(event.target.value);
                        }}
                        renderValue={(value) => {
                          const menu = menusSearchType.find(({ action }) => action === value);
                          return (
                            <Box display='flex' alignItems='center'>
                              <span className={classes.selectedValue}>{menu.label}</span>
                            </Box>
                          );
                        }}
                        value={currentSearchType}>
                        {menusSearchType.map(({ action, icon, label }) => (
                          <DocumentMenu.Item
                            key={action}
                            action={action}
                            icon={icon}
                            onClickMenuItem={(action) => {
                              setCurrentSearchType(action);
                              onChange(search.value, { searchType: action });
                              onFocus();
                            }}
                            value={action}
                            label={label}></DocumentMenu.Item>
                        ))}
                      </Select>
                    </Box>
                  }
                />
              );
            }}
          </SearchWithSuggestionProvider>
        </Box>
      </Box>
      <Box display='flex' alignItems='center'>
        <Header
          disabled={editMode}
          title='Document'
          onClickMore={onClickMore}
          focusing={showMore}
        />
        {editMode && (
          <Button
            className={classes.button}
            variant='contained'
            onClick={() => {
              setEditMode(false);
            }}>
            Done
          </Button>
        )}
      </Box>
      <Popover
        PaperProps={{ className: classes.wrapMenu }}
        disableAutoFocus
        disableEnforceFocus
        open={showMore}
        anchorEl={anchorEl}
        onClose={() => {
          setShowMore(false);
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <DocumentMenu
          menus={tabs.length > 3 ? MENUS : [MENUS[0]]}
          onClickMenuItem={(action) => {
            setShowMore(false);
            setAnchorEl(null);
            if (action === 'new-tab') {
              setAddingNewTab(true);
            }
            if (action === 'edit-tab') {
              setEditMode(true);
            }
          }}
        />
      </Popover>

      <Tabs
        currentTab={currentTab}
        onChangeTab={(nextTab) => {
          setCurrentTab(nextTab);
          const tab = tabs[nextTab];
          setFolders([]);
          setSelected({ ...selected, [activeTab]: [] });
          setActiveTab(tab.label);
        }}
        disabled={editMode}
        className={classes.tabs}
        color='primary'
        scrollButtons='on'
        variant='scrollable'
        ScrollButtonComponent={(props, ref) => {
          const { direction, orientation, ...rest } = props;
          if (direction === 'left') {
            leftButtonProps.current = { ...rest };
            return null;
          }
          return (
            <>
              <IconButton {...leftButtonProps.current}>
                <ChevronLeftIcon></ChevronLeftIcon>
              </IconButton>
              <IconButton {...rest}>
                <NavigateNextIcon></NavigateNextIcon>
              </IconButton>
            </>
          );
        }}
        TabIndicatorProps={{
          style: {
            display: 'none',
          },
        }}
        tabs={tabs.map(({ editable, label, key }, index) => ({
          content: (
            <ProductFileManagementProvider productId={productId} onRefresh={onRefresh}>
              <FileManagement
                reloadData={props.reloadData}
                activeTab={activeTab}
                selectedShare={selected}
                selectToShare={setSelected}
                onRename={handleRename}
                onMoveFileFolder={handleMoveFileFolder}
                onShareFile={() => {
                  setAction('shareFile');
                  setFilesNav(true);
                }}
                onBulkDownload={() => {
                  setAction('bulkDownload');
                  setFilesNav(true);
                }}
                onUpdateExpiryDate={handleUpdateExpiryDate}
                onUpdateReminder={handleUpdateReminder}
                onUploadFiles={onCreateFiles}
                currentFolder={currentFolder}
                onOpenFolder={(nextFolder) => {
                  folders.push({
                    id: nextFolder.id,
                    parentId: nextFolder.parentId,
                    label: nextFolder.name,
                  });
                  setFolders([...folders]);
                }}
                onDeleteFileFolder={handleDeleteFileOrFolder}
                onCreateFolder={onCreateFolder}
                data={get(files, `${key}.fileArr`)}
                files={files}
                disabled={editMode}
                isActive={isActive}
                onOpenMarkActiveModal={onOpenMarkActiveModal}
              />
            </ProductFileManagementProvider>
          ),
          label: !editable ? (
            label
          ) : (
            <Box
              className={classes.tabEditable}
              onClick={(event) => {
                if (!editMode) return;
                event.preventDefault();
                event.stopPropagation();
                setEditItem({
                  id: index,
                  value: label,
                });
                setAddingNewTab(true);
              }}>
              {label}
              {editMode && <EditIcon />}
            </Box>
          ),
        }))}
      />

      <AddNewModal
        onDelete={() => {
          setShowDeleteModal(true);
          setAddingNewTab(false);
        }}
        placeholder={T.translate('modal.tab.form.placeholder')}
        title={
          editItem && editMode
            ? T.translate('modal.tab.form.title.edit')
            : T.translate('modal.tab.form.title.new')
        }
        tabs={Object.keys(files)}
        open={addingNewTab}
        handleClose={() => {
          setAddingNewTab(false);
          setEditItem(null);
        }}
        item={editItem}
        createNew={(newTab, record) => {
          const fnCallback = () => {
            setAddingNewTab(false);
          };
          if (record) {
            const newTabs = tabs.map((tab, index) => {
              return {
                ...tab,
                label: record.id === index ? newTab : tab.label,
              };
            });
            setTabs([...newTabs]);
            setEditItem(null);
            onUpdateTab(
              {
                oldName: record.value,
                newName: newTab,
              },
              fnCallback
            );
          } else {
            onCreateTab(
              productId,
              {
                productId,
                tabName: newTab,
              },
              fnCallback
            );
            setTabs([...tabs, { label: newTab, editable: true }]);
          }
          enqueueSnackbar(`${record ? 'Update' : 'Created'} ${newTab} Tab`, {
            key: 'tab-success',
            variant: 'success',
            preventDuplicate: true,
            className: classes.snackbarSuccess,
            autoHideDuration: 3000,
            action: (
              <IconButton
                onClick={() => {
                  closeSnackbar('tab-success');
                }}>
                <CloseIcon />
              </IconButton>
            ),
          });
        }}
      />
      <DeleteModal
        open={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setEditItem(null);
        }}
        item={editItem}
        onDelete={handleDeleteTab}
        title='Delete Tab?'
        body={
          <Box className={classes.messageDelete}>
            <Typography variant='body1'>
              You’ll also delete all items in this tab, including:
            </Typography>
            <ul>
              <li>
                {get(files, `${editItem?.value}.fileArr`, []).filter(({ url }) => !url).length}{' '}
                folders
              </li>
              <li>
                {get(files, `${editItem?.value}.fileArr`, []).filter(({ url }) => !!url).length}{' '}
                files
              </li>
            </ul>
          </Box>
        }
      />

      {
        // Reuse Modal from Old UI.
      }
      <FileSystemNavigator
        open={openFilesNav}
        setOpen={setFilesNav}
        action={action}
        filesInit={{
          inspection: [],
          testing: [],
          design: [],
          materials: [],
          shipping: [],
          manufacturing: [],
          protocol: [],
        }}
        files={transformFiles}
        selected={selected}
        setSelected={setSelected}
        setShareFiles={setShareFiles}
        handleBulkDownload={() => {
          const ids = flattenDeep(
            Object.keys(selected).map((key) => selected[key].filter((file) => file.id))
          );
          onBulkDownload({ docIds: ids });
        }}
      />
      <ShareFiles
        id={productId}
        open={openShareFiles}
        setOpen={setShareFiles}
        setFilesNav={setFilesNav}
        companyId={companyId}
        selected={selected}
        setSelected={setSelected}
        onShare={(values, link, companyId) => {
          shareFile({
            value: values,
            link,
            companyId,
          });
        }}
      />
    </Box>
  );
};
export default DocumentSections;
